import { evidenceSidebarOpenAtom } from '@/atoms/evidenceSidebarAtoms';
import { activeSessionIdAtom } from '@/atoms/liveTranscriptionAtom';
import {
  useIsAmbientHeidiEnabled,
  useIsVoiceAssistantEnabled,
  useIsVoiceWorkspaceEnabled,
} from '@/features/flags';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { playAmbientHeidiEarcon } from '@/app-shell/ambient-heidi-earcon';
import { useAmbientVoiceBridge } from '@heidi/agents';
import { useIsAuthenticated } from '@heidi/agents/data';
import { voiceController } from '../controller';
import { useVoiceGuideRunner } from '../help/useVoiceGuideRunner';
import { useVoiceGuideStore } from '../help/useVoiceGuideStore';
import { useVoiceHighlightStore } from '../help/useVoiceHighlightStore';
import { VoiceHighlightOverlay } from '../help/VoiceHighlightOverlay';
import { useApplyVoiceSessionFocus } from '../hooks/useApplyVoiceSessionFocus';
import { useApplyVoiceSessionPanel } from '../hooks/useApplyVoiceSessionPanel';
import { useSyncVoiceActiveTab } from '../hooks/useSyncVoiceActiveTab';
import { useVoiceAssistant } from '../hooks/useVoiceAssistant';
import { useVoiceAssistantStore } from '../store/useVoiceAssistantStore';
import { useVoicePlacement } from '../workspace/voicePlacement';
import { VoiceWorkspaceDevTrigger } from '../workspace/VoiceWorkspaceDevTrigger';
import { VoiceWorkspaceOverlay } from '../workspace/VoiceWorkspaceOverlay';
import { WorkspaceMinimizedPill } from '../workspace/WorkspaceMinimizedPill';
import { shouldMinimizeVoiceForPath } from './shouldMinimizeVoiceForPath';
import { VoiceAssistantPanel } from './VoiceAssistantPanel';

const VoiceGuideHost = () => {
  useVoiceGuideRunner();
  return null;
};

/** Session tab/template sync + open_artifact focus — only mounts when voice is on. */
const VoiceSessionBridge = () => {
  useSyncVoiceActiveTab();
  useApplyVoiceSessionFocus();
  useApplyVoiceSessionPanel();
  return null;
};

const AmbientVoiceBridgeMount = () => {
  const flagEnabled = useIsAmbientHeidiEnabled();
  if (!flagEnabled) {
    return null;
  }
  return <AmbientVoiceBridgeActive />;
};

const AmbientVoiceBridgeActive = () => {
  const { open } = useVoiceAssistant();
  const connection = useVoiceAssistantStore((state) => state.connection);
  const voiceMicLive = useVoiceAssistantStore((state) => state.micStream !== null);
  const visitRecording = Boolean(useAtomValue(activeSessionIdAtom));
  useAmbientVoiceBridge({
    open,
    connection,
    voiceMicLive,
    visitRecording,
    onTriggered: playAmbientHeidiEarcon,
  });
  return null;
};

/**
 * Root gate for the global voice assistant. Renders nothing unless the feature
 * flag is on and the user is authenticated. Mounted once, app-wide, in
 * DeferredAppShellWidgets — deliberately not session-scoped.
 *
 * Presentation (LHS rail + bottom pill) stays behind this gate so Work / CUA
 * overlays can coexist without importing voice internals. The entry point
 * (VoiceAssistantTrigger) lives beside the Ask Heidi bar; this component hosts
 * chrome + teardown only.
 *
 * Follow-up (this branch): keep the WS session alive across session-page
 * remounts / mic handoffs via context_update instead of stop()+restart, and
 * keep highlight overlay measure on rAF (no scrollIntoView feedback loop).
 */
// Local dev: allow the workspace overlay to mount + expose the dev trigger even
// when release-scribe-talk-to-heidi is off, so it can be exercised without a
// backend. Folded to `false` (and dead-code-eliminated) in production builds.
const IS_DEV_BUILD = process.env.NODE_ENV !== 'production';

export const GlobalVoiceAssistant = () => {
  const isEnabled = useIsVoiceAssistantEnabled();
  const isWorkspaceEnabled = useIsVoiceWorkspaceEnabled();
  const voicePlacement = useVoicePlacement();
  const evidenceSidebarOpen = useAtomValue(evidenceSidebarOpenAtom);
  const isAuthenticated = useIsAuthenticated();
  const workspace = useVoiceAssistantStore((state) => state.workspace);
  const { pathname } = useRouter();

  const workspaceAllowed = isWorkspaceEnabled || IS_DEV_BUILD;

  // Workspace shows only when its capability flag is on (or in a dev build). If
  // the flag flips off mid-session, drop any open workspace so state can't
  // linger unmounted.
  useEffect(() => {
    if (!workspaceAllowed && useVoiceAssistantStore.getState().workspace) {
      useVoiceAssistantStore.getState().closeWorkspace();
    }
  }, [workspaceAllowed]);

  // Leaving clinical surfaces for Settings (etc.) flips placement to `overlay`.
  // Without this, the expanded Voice window dual-paints on top of Settings.
  // Keep the session alive — only collapse chrome to the dock / restore pill.
  useEffect(() => {
    if (!shouldMinimizeVoiceForPath(pathname)) return;
    const state = useVoiceAssistantStore.getState();
    if (state.workspace && !state.workspace.minimized) {
      state.minimizeWorkspace();
    }
  }, [pathname]);

  // The controller is a module-level singleton holding the live mic stream and
  // websocket. If the user logs out (token clears) or the flag flips off
  // mid-session, this gate unmounts the modal but the singleton would keep
  // capturing. Tear it down when the session can no longer be controlled, and
  // on unmount. stop() is idempotent (no-op when inactive).
  useEffect(() => {
    if (!isEnabled || !isAuthenticated) {
      voiceController.stop();
      // Also clear the open flag: otherwise re-enabling the flag would
      // remount the modal already open on a dead session.
      useVoiceAssistantStore.getState().close();
      useVoiceGuideStore.getState().clearGuide();
      useVoiceHighlightStore.getState().clearHighlight();
    }
  }, [isEnabled, isAuthenticated]);

  useEffect(
    () => () => {
      voiceController.stop();
      useVoiceGuideStore.getState().clearGuide();
      useVoiceHighlightStore.getState().clearHighlight();
    },
    []
  );

  if (!isEnabled || !isAuthenticated) return null;

  const workspaceActive = workspaceAllowed && !!workspace;
  const workspaceExpanded = workspaceActive && !workspace.minimized;
  // Host owns paint when placement is session-rhs *and* the RHS is open
  // (VoiceWindow mounts inside the open panel). Until Sync opens it — or if
  // the host never opens — keep the overlay so Voice cannot vanish.
  const overlayExpanded =
    workspaceExpanded &&
    (voicePlacement === 'overlay' ||
      (voicePlacement === 'session-rhs' && !evidenceSidebarOpen));
  const showDock = !workspaceExpanded;

  return (
    <>
      <AmbientVoiceBridgeMount />
      <VoiceSessionBridge />
      {/* Overlay (or host-embedded VoiceWindow) owns the composer while
          expanded; the dock returns when minimised, alongside the restore pill. */}
      {overlayExpanded ? <VoiceWorkspaceOverlay /> : null}
      {showDock ? <VoiceAssistantPanel /> : null}
      {workspaceActive && workspace.minimized ? <WorkspaceMinimizedPill /> : null}
      {IS_DEV_BUILD ? <VoiceWorkspaceDevTrigger /> : null}
      <VoiceGuideHost />
      <VoiceHighlightOverlay />
    </>
  );
};
